import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Inbox, Loader2, Mail, Search, Send, Eye } from "lucide-react";
import { apiFetch, postReply, ReplyBox, ReplyHistory, DetailModal } from "./bits";

const SUBJECT_LABELS = {
  general: "General inquiry",
  buyer: "Buyer early access",
  vendor: "Vendor & partnerships",
  other: "Other",
};

export const EnquiriesView = ({ refreshKey, onAuthFail }) => {
  const [enquiries, setEnquiries] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [subject, setSubject] = useState("all");
  const [detail, setDetail] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replySending, setReplySending] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);

  const load = useCallback(async (query, subj) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (query && query.trim()) params.set("q", query.trim());
      if (subj && subj !== "all") params.set("subject", subj);
      const res = await apiFetch(`/admin/enquiries?${params.toString()}`);
      if (res.ok) setEnquiries(await res.json());
      else if (res.status === 401) onAuthFail();
      else toast.error("Could not load enquiries.");
    } catch {
      toast.error("Could not load enquiries.");
    } finally {
      setLoading(false);
    }
  }, [onAuthFail]);

  useEffect(() => { load(q, subject); }, [refreshKey, subject]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSearch = (e) => {
    e.preventDefault();
    load(q, subject);
  };

  const openDetail = async (id) => {
    try {
      const res = await apiFetch(`/admin/enquiries/${id}`);
      if (res.ok) {
        setDetail(await res.json());
        setReplyOpen(false);
        setReplyText("");
      } else if (res.status === 401) onAuthFail();
      else toast.error("Could not open the record.");
    } catch {
      toast.error("Could not open the record.");
    }
  };

  const sendReply = async () => {
    if (!detail) return;
    setReplySending(true);
    try {
      const reply = await postReply("enquiry", detail.id, replyText);
      setDetail((d) => ({ ...d, replies: [...(d.replies || []), reply] }));
      setEnquiries((prev) => prev && prev.map((r) => (r.id === detail.id ? { ...r, replies: [...(r.replies || []), reply] } : r)));
      setReplyOpen(false);
      setReplyText("");
      toast.success("Reply sent.", { description: "Your reply was emailed and saved to the thread." });
    } catch (err) {
      toast.error("Could not send reply.", { description: err.message });
    } finally {
      setReplySending(false);
    }
  };

  return (
    <div className="mt-12" data-testid="admin-enquiries-view">
      <div className="flex flex-wrap items-center gap-3">
        <form onSubmit={onSearch} className="flex min-w-0 flex-1 gap-2" data-testid="enquiries-search-form">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="enquiries-search-input"
            className="field !py-2.5 !text-sm"
            placeholder="Search name, email or message…"
            aria-label="Search enquiries"
          />
          <button type="submit" data-testid="enquiries-search-button" className="btn-ghost shrink-0 !px-5 !py-2.5 !text-xs">
            <Search size={14} /> Search
          </button>
        </form>
        <div className="flex flex-wrap gap-2" data-testid="enquiries-filters">
          {["all", "general", "buyer", "vendor", "other"].map((s) => (
            <button
              key={s}
              onClick={() => setSubject(s)}
              data-testid={`enquiries-filter-${s}`}
              className={`rounded-full border px-4 py-2 font-sans text-[10px] tracking-[0.2em] transition-colors duration-300 ${
                subject === s ? "border-[#1E3A8A]/60 bg-[#1E3A8A]/10 text-[#1E3A8A]" : "border-[#E2E8F0] text-[#94A3B8] hover:border-[#1E3A8A]/30 hover:text-[#1E3A8A]/75"
              }`}
            >
              {s === "all" ? "ALL" : SUBJECT_LABELS[s].toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-8" data-testid="admin-enquiries-list">
        {enquiries === null || (loading && enquiries === null) ? (
          <div className="flex justify-center py-16"><Loader2 size={24} className="animate-spin text-[#1E3A8A]" /></div>
        ) : enquiries.length === 0 ? (
          <div className="card-dark flex flex-col items-center p-16 text-center hover:transform-none" data-testid="admin-empty-state">
            <Inbox size={28} className="text-[#1E3A8A]/60" />
            <p className="mt-4 font-sans text-xl text-[#0F172A]">No enquiries found</p>
            <p className="mt-2 text-sm text-[#94A3B8]">New contact-form messages will appear here.</p>
          </div>
        ) : (
          <div className="space-y-5">
            <p className="font-sans text-xs tracking-[0.25em] text-[#94A3B8]" data-testid="admin-enquiries-count">
              {enquiries.length} ENQUIR{enquiries.length === 1 ? "Y" : "IES"}
            </p>
            {enquiries.map((q2, i) => (
              <article key={q2.id || i} data-testid={`enquiry-card-${i}`} className="card-dark p-6 sm:p-8 hover:transform-none">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <span className="rounded-full border border-[#1E3A8A]/30 px-3 py-1 font-sans text-[10px] tracking-[0.2em] text-[#1E3A8A]/80">
                    {(SUBJECT_LABELS[q2.subject] || q2.subject || "General").toUpperCase()}
                  </span>
                  <time className="font-sans text-[11px] text-[#94A3B8]">{q2.created_at ? new Date(q2.created_at).toLocaleString() : ""}</time>
                </div>
                <div className="mt-4 flex flex-wrap items-baseline gap-x-3">
                  <h3 className="font-sans text-lg text-[#0F172A]">{q2.name}</h3>
                  <a href={`mailto:${q2.email}`} data-testid={`enquiry-email-${i}`} className="inline-flex items-center gap-1.5 text-sm text-[#1E3A8A]/80 transition-colors hover:text-[#1E3A8A]">
                    <Mail size={13} /> {q2.email}
                  </a>
                </div>
                <p className="mt-3 truncate text-sm leading-relaxed text-[#64748B]">{q2.message}</p>
                <div className="mt-4">
                  <button onClick={() => openDetail(q2.id)} data-testid={`enquiry-open-${i}`} className="btn-ghost !px-5 !py-2 !text-xs">
                    <Eye size={13} /> View Record
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      {detail && (
        <DetailModal title={detail.name} onClose={() => setDetail(null)} testId="enquiry-detail-modal">
          <div className="flex flex-wrap items-center gap-3">
            <span className="rounded-full border border-[#1E3A8A]/30 px-3 py-1 font-sans text-[10px] tracking-[0.2em] text-[#1E3A8A]/80">
              {(SUBJECT_LABELS[detail.subject] || detail.subject || "General").toUpperCase()}
            </span>
            <time className="font-sans text-[11px] text-[#94A3B8]">{detail.created_at ? new Date(detail.created_at).toLocaleString() : ""}</time>
          </div>
          <dl className="mt-6 space-y-4 text-sm">
            <div>
              <dt className="font-sans text-[10px] tracking-[0.2em] text-[#94A3B8]">EMAIL</dt>
              <dd className="mt-1">
                <a href={`mailto:${detail.email}`} data-testid="enquiry-detail-email" className="inline-flex items-center gap-1.5 text-[#1E3A8A]/80 transition-colors hover:text-[#1E3A8A]">
                  <Mail size={13} /> {detail.email}
                </a>
              </dd>
            </div>
            <div>
              <dt className="font-sans text-[10px] tracking-[0.2em] text-[#94A3B8]">MESSAGE</dt>
              <dd className="mt-1 whitespace-pre-wrap leading-relaxed text-[#475569]" data-testid="enquiry-detail-message">{detail.message}</dd>
            </div>
            <div>
              <dt className="font-sans text-[10px] tracking-[0.2em] text-[#94A3B8]">RECORD ID</dt>
              <dd className="mt-1 font-sans text-xs text-[#94A3B8]" data-testid="enquiry-detail-id">{detail.id}</dd>
            </div>
          </dl>
          <ReplyHistory replies={detail.replies} testId="detail" />
          <div className="mt-4">
            {replyOpen ? (
              <ReplyBox
                testId="detail"
                sending={replySending}
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                onSend={sendReply}
                onCancel={() => { setReplyOpen(false); setReplyText(""); }}
              />
            ) : (
              <button onClick={() => { setReplyOpen(true); setReplyText(""); }} data-testid="reply-open-detail" className="btn-ghost !px-5 !py-2 !text-xs">
                <Send size={13} /> Reply
              </button>
            )}
          </div>
        </DetailModal>
      )}
    </div>
  );
};
